import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {TranslateModule} from '@ngx-translate/core';
import {
    provideNgxWebstorage,
    withLocalStorage,
    withNgxWebstorageConfig,
    withSessionStorage
} from 'ngx-webstorage';
import {CommonModule, DatePipe} from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {AutosizeModule} from 'ngx-autosize';
import {TrackingModule} from '../services/tracking/tracking.module';
import {NgxFileDropModule} from 'ngx-file-drop';

import {
    ApiService,
    LibraryService,
    LoadingSpinnerService,
    ToastService,
    ProjectService,
    ProjectMandateService,
    QuickscanDefinitionService,
    StepService,
    TisDefinitionService,
    TranslationService,
    UserService,
    FilterService,
    PdfService,
    ImageService,
    CompanyTypeService,
    TenderManagerService,
    CompanyService,
    NgSelectService,
    NotificationService,
    AzureAdRegistrationService,
    UserSearchService
} from '../services';

import {TRANSLATION_NL} from '../translations';

import {
    PageLogoutComponent,
    PageNoAccessComponent,
    PageErrorComponent,
    PageOpportunitiesLibraryComponent,
    PageTasksLibraryComponent,
    PageProjectsComponent,
    PageProjectStepsComponent,
    PageQuickscanComponent,
    PageQuickscansComponent,
    PageRisksLibraryComponent,
    PageStartComponent,
    PageTisComponent,
    PageTisversionComponent,
    PagePdfPortraitComponent,
    PagePdfLandscapeComponent,
    PageTenderManagersComponent,
    PageCompaniesComponent,
    PageCompanyTypesComponent,
    PageNotificationsComponent,
    PageUserManagementComponent,
    PageAccessibleStepManagementComponent
} from '../pages/';

import {AppComponent} from './app.component';

import {
    AddProjectLibraryItemsModalComponent,
    CreateProjectModalComponent,
    CreateUpdateLibraryCategoryModalComponent,
    CreateUpdateLibraryItemModalComponent,
    DeleteItemModalComponent,
    EditRemarkModalComponent,
    InlineNotificationComponent,
    ItemDescriptionTabComponent,
    ItemQuantificationTabComponent,
    ItemRiskMitigationTabComponent,
    LibraryMgmtComponent,
    LoadingSpinnerComponent,
    LoadingSpinnerFullscreenComponent,
    ProgressBarComponent,
    ProjectDetailsComponent,
    ProjectQuickscanComponent,
    ProjectTisQuickscanComponent,
    QuickscanListComponent,
    ProjectQuickscanResultComponent,
    ProjectRisksComponent,
    ProjectOpportunitiesComponent,
    ProjectTasksComponent,
    ProjectMandatingComponent,
    ProjectLibraryItemsComponent,
    PublishQuickScanVersionModalComponent,
    PublishTisModalComponent,
    QuickscanCreateUpdateCategoryModalComponent,
    QuickscanCreateUpdateQuestionModalComponent,
    ChartSignalMatrixComponent,
    BlockStatsComponent,
    BlockSignalComponent,
    StickyHeaderComponent,
    TisCreateUpdateCategoryModalComponent,
    TisCreateUpdateQuestionModalComponent,
    ScrollNavigationComponent,
    MenuComponent,
    NavItemComponent,
    ItemCardComponent,
    ProjectRiskRainbowComponent,
    ProjectRiskDossierComponent,
    ProjectTisComponent,
    QuestionAttachmentsComponent,
    QuestionInputComponent,
    QuantifySupplementProjectLibraryItemModalComponent,
    LibraryItemQuantificationGridComponent,
    ExplanationComponent,
    ItemStatusComponent,
    ShareProjectModalComponent,
    PdfPageComponent,
    PdfPagebreakComponent,
    PdfPageHeaderComponent,
    PdfPageFooterComponent,
    PdfPageProjectInfoComponent,
    PdfPageProjectTisComponent,
    DownloadPDFModalComponent,
    PdfPageQuickscanResultComponent,
    PdfPageQuickscanComponent,
    PdfPageTisQuickscanComponent,
    PdfPageQuickscanListComponent,
    PdfPageRiskRainbowComponent,
    PdfPageRisksAndOpportunitiesComponent,
    ScoreSignalBadgeComponent,
    CreateUpdateTenderManagerModalComponent,
    CreateUpdateCompanyModalComponent,
    CreateUpdateCompanyTypeModalComponent,
    ClassificationComponent,
    CloseProjectModalComponent,
    DeleteProjectModalComponent,
    ExecuteProjectModalComponent,
    PdfPageForecastEndResultListComponent,
    PdfPageForecastEndResultListTableComponent,
    PdfPageMandatingFlowComponent,
    PdfPageRisksAndOpportunitiesContentComponent,
    LibraryItemFiltersComponent,
    LibraryItemFiltersOptionsComponent,
    LibraryItemFiltersSelectedComponent,
    LibraryItemFiltersSelectedButtonComponent,
    LibraryItemFiltersWithToggleComponent,
    UpdateNotificationModalComponent,
    NotificationModalComponent,
    InputCounterComponent,
    VersionComponent,
    UserIdentityInputComponent,
    MandatingFlowInfoCardComponent,
    InputHintComponent,
    ChangeUserModalComponent,
    ProjectStatusComponent,
    ProjectOnHoldModalComponent,
    RadioGroupComponent
} from '../components';

import {
    CategoryGetAllSubCategoryQuestionsPipe,
    DateFormatPipe,
    SafeHTMLPipe,
    EscapeHTMLPipe,
    PriceFormatPipe,
    TrimPipe,
    NamesFormatPipe,
    IsValidEmailAddressPipe,
    Nl2BrPipe
} from '../pipes';

import {environment} from 'src/environments/environment';
import {ProjectStepGuard, UserGuard, UserRoleGuard} from '../guards';
import {VwuiAngularModule} from '@bvgo-ict/vwui-angular';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {ToastrModule} from 'ngx-toastr';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {FormatEuroCentsDirective} from '../directives/format-euro-cents.directive';
import {DisabledReasonDirective} from '../directives/disabled-reason.directive';
import {ClickOutsideDirective} from '../directives/click-outside.directive';
import {VwuiTableDefaultBreakpointDirective} from '../directives/vwui-table-default-breakpoint.directive';
import {MsAuthModule} from 'src/module/ms-auth';
import {RichTextEditorModule} from '../module/rich-text-editor/module';
import {UiAccessibilityModule} from '../module/ui-accessibility';
import {AccessibleStepsService} from '../services/accessible-steps/accessible-steps.service';
import {fetchAccessibleSteps} from '../initializers/accessible-steps.initializer';

@NgModule({ declarations: [
        FormatEuroCentsDirective,
        DisabledReasonDirective,
        ClickOutsideDirective,
        VwuiTableDefaultBreakpointDirective,
        AppComponent,
        InlineNotificationComponent,
        PageProjectStepsComponent,
        ProjectDetailsComponent,
        ProjectQuickscanComponent,
        ProjectTisQuickscanComponent,
        QuickscanListComponent,
        ProjectQuickscanResultComponent,
        ScrollNavigationComponent,
        ProjectRisksComponent,
        ProjectOpportunitiesComponent,
        ProjectTasksComponent,
        ProjectLibraryItemsComponent,
        ProjectMandatingComponent,
        ProgressBarComponent,
        PageStartComponent,
        PageNoAccessComponent,
        PageErrorComponent,
        PageProjectsComponent,
        PageLogoutComponent,
        PageNotificationsComponent,
        LibraryMgmtComponent,
        LoadingSpinnerComponent,
        LoadingSpinnerFullscreenComponent,
        AddProjectLibraryItemsModalComponent,
        CreateUpdateLibraryCategoryModalComponent,
        CreateUpdateLibraryItemModalComponent,
        DeleteItemModalComponent,
        CreateProjectModalComponent,
        EditRemarkModalComponent,
        DateFormatPipe,
        PageQuickscansComponent,
        PageRisksLibraryComponent,
        PageOpportunitiesLibraryComponent,
        PageTasksLibraryComponent,
        PageTisComponent,
        PageTisversionComponent,
        PublishQuickScanVersionModalComponent,
        PublishTisModalComponent,
        PageQuickscanComponent,
        QuickscanCreateUpdateCategoryModalComponent,
        ItemDescriptionTabComponent,
        ItemQuantificationTabComponent,
        ItemRiskMitigationTabComponent,
        QuickscanCreateUpdateQuestionModalComponent,
        TisCreateUpdateCategoryModalComponent,
        TisCreateUpdateQuestionModalComponent,
        ChartSignalMatrixComponent,
        BlockStatsComponent,
        BlockSignalComponent,
        StickyHeaderComponent,
        PagePdfPortraitComponent,
        PagePdfLandscapeComponent,
        PageTenderManagersComponent,
        PageCompaniesComponent,
        PageCompanyTypesComponent,
        SafeHTMLPipe,
        EscapeHTMLPipe,
        MenuComponent,
        NavItemComponent,
        ItemCardComponent,
        ProjectRiskRainbowComponent,
        ProjectRiskDossierComponent,
        ProjectTisComponent,
        QuestionAttachmentsComponent,
        QuestionInputComponent,
        QuantifySupplementProjectLibraryItemModalComponent,
        LibraryItemQuantificationGridComponent,
        ExplanationComponent,
        ItemStatusComponent,
        ShareProjectModalComponent,
        Nl2BrPipe,
        PdfPageComponent,
        PdfPagebreakComponent,
        PdfPageHeaderComponent,
        PriceFormatPipe,
        PdfPageProjectInfoComponent,
        PdfPageProjectTisComponent,
        DownloadPDFModalComponent,
        PdfPageQuickscanResultComponent,
        PdfPageQuickscanComponent,
        PdfPageTisQuickscanComponent,
        PdfPageQuickscanListComponent,
        PdfPageRiskRainbowComponent,
        PdfPageRisksAndOpportunitiesComponent,
        ScoreSignalBadgeComponent,
        CreateUpdateTenderManagerModalComponent,
        CreateUpdateCompanyModalComponent,
        CreateUpdateCompanyTypeModalComponent,
        PdfPageFooterComponent,
        ClassificationComponent,
        CloseProjectModalComponent,
        DeleteProjectModalComponent,
        ExecuteProjectModalComponent,
        PdfPageForecastEndResultListComponent,
        PdfPageForecastEndResultListTableComponent,
        PdfPageMandatingFlowComponent,
        PdfPageRisksAndOpportunitiesContentComponent,
        LibraryItemFiltersComponent,
        LibraryItemFiltersOptionsComponent,
        LibraryItemFiltersSelectedComponent,
        LibraryItemFiltersSelectedButtonComponent,
        LibraryItemFiltersWithToggleComponent,
        UpdateNotificationModalComponent,
        NotificationModalComponent,
        InputCounterComponent,
        TrimPipe,
        VersionComponent,
        CategoryGetAllSubCategoryQuestionsPipe,
        NamesFormatPipe,
        UserIdentityInputComponent,
        MandatingFlowInfoCardComponent,
        InputHintComponent,
        PageUserManagementComponent,
        PageAccessibleStepManagementComponent,
        ChangeUserModalComponent,
        IsValidEmailAddressPipe,
        ProjectStatusComponent,
        ProjectOnHoldModalComponent,
        RadioGroupComponent
    ],
    bootstrap: [
        AppComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        MsAuthModule.forRoot(environment.msAuth),
        AppRoutingModule,
        TranslateModule.forRoot(),
        ToastrModule.forRoot(),
        VwuiAngularModule.forRoot(),
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        AutosizeModule,
        TrackingModule,
        NgxFileDropModule,
        CommonModule,
        RichTextEditorModule,
        DragDropModule,
        UiAccessibilityModule.forRoot()],
    providers: [
        ApiService,
        LibraryService,
        LoadingSpinnerService,
        ToastService,
        ProjectService,
        provideNgxWebstorage(
            withNgxWebstorageConfig({ prefix: 'RISKMONITOR', separator: '_', caseSensitive: true }),
            withLocalStorage(),
            withSessionStorage()
        ),
        ProjectMandateService,
        ImageService,
        QuickscanDefinitionService,
        TisDefinitionService,
        TranslationService,
        FilterService,
        UserService,
        StepService,
        AccessibleStepsService,
        DatePipe,
        UserGuard,
        UserRoleGuard,
        ProjectStepGuard,
        PdfService,
        CompanyTypeService,
        TenderManagerService,
        CompanyService,
        NgSelectService,
        NotificationService,
        AzureAdRegistrationService,
        UserSearchService,
        provideAppInitializer(() => {
        const initializerFn = (fetchAccessibleSteps)(inject(AccessibleStepsService));
        return initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
    private translationService = inject(TranslationService);
    private ngSelectService = inject(NgSelectService);
    public static injector: Injector;

    constructor() {
        const injector = inject(Injector);
        const translationService = this.translationService;
        const ngSelectService = this.ngSelectService;

        AppModule.injector = injector;

        translationService.add({
            nl: TRANSLATION_NL
        });
        translationService.set('nl');
        translationService.addCountries();
        ngSelectService.init();
    }

}
